import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './viewTenant.module.css';
import { useHasPermission } from '../../../contexts/auth';

export const formatDate = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

export const TenantConfig = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const canViewSubscription = useHasPermission("payment.view_tenantsubscription");
  const canViewUsers = useHasPermission("users.view_user");
  const canViewGroups = useHasPermission("auth.view_group");
  const canViewSettings = useHasPermission("config.view_tenantsettings");
  const canViewSchemas = useHasPermission("settlement.view_numerationschema");
  const canViewTransitionActions = useHasPermission("service.view_jobstatustransitionaction");
  const canViewAutomation = useHasPermission("settlement.view_contract");

  const visibleTabs = [
    { key: 'basic', path: '/tenant' },
    ...(canViewSubscription ? [{ key: 'subscription', path: '/tenant/subscription' }] : []),
    ...(canViewSettings ? [{ key: 'settings', path: '/tenant/settings' }] : []),
    ...(canViewUsers ? [{ key: 'users', path: '/tenant/users' }] : []),
    ...(canViewGroups ? [{ key: 'groups', path: '/tenant/permission-groups' }] : []),
    ...(canViewSchemas ? [{ key: 'numeration-schemas', path: '/tenant/numeration-schemas' }] : []),
    ...(canViewTransitionActions ? [{ key: 'status-transition-actions', path: '/tenant/status-transition-actions' }] : []),
    ...(canViewAutomation ? [{ key: 'automation', path: '/tenant/automation' }] : []),
  ];

  const getTabValue = () => {
    const pathname = location.pathname;

    if (pathname.includes('/tenant/users/') && !pathname.endsWith('/users')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'users');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/permission-groups/') && !pathname.endsWith('/permission-groups')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'groups');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/numeration-schemas/') && !pathname.endsWith('/numeration-schemas')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'numeration-schemas');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/status-transition-actions')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'status-transition-actions');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/automation')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'automation');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/subscription')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'subscription');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/users')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'users');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/permission-groups')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'groups');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/numeration-schemas')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'numeration-schemas');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/tenant/settings')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'settings');
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = visibleTabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    } else {
      navigate('/tenant');
    }
  };

  return (
    <Box className={styles.container}>
      <Tabs
        value={getTabValue()}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map((tab) => {
          const labels: Record<string, string> = {
            basic: t("Basic Information"),
            subscription: t("Subscription"),
            settings: t("Settings"),
            users: t("Users"),
            groups: t("Permission Groups"),
            'numeration-schemas': t("Numeration Schemas"),
            'status-transition-actions': t("Status Transition Actions"),
            automation: t("Settlement Automation"),
          };
          return <Tab key={tab.key} label={labels[tab.key]} />;
        })}
      </Tabs>
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
